import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Rating,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import apiClient from '../config/axios';
import ModalConfirm from './ModalConfirm';
import ModalResult from './ModalResult';

const ModalProposedGrade = ({ open, onClose, wallId, onSuccess, editData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    grade: '',
    notes: '',
    rating: 1
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState({ message: '', severity: 'info' });

  // Popola il form quando è in modalità edit
  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        grade: editData.grade || '',
        notes: editData.notes || '',
        rating: editData.rating || 1
      });
    } else {
      setFormData({
        grade: '',
        notes: '',
        rating: 1
      });
    }
  }, [isEdit, editData, open]);

  // Gradi disponibili da 5a a 7b
  const availableGrades = [
    
    '4a', '4a+', '4b', '4b+', '4c', '4c+',
    '5a', '5a+', '5b', '5b+', '5c', '5c+',
    '6a', '6a+', '6b', '6b+', '6c', '6c+',
    '7a', '7a+', '7b', '7b+', '7c', '7c+',
    '8a', '8a+', '8b', '8b+', '8c', '8c+',
    '9a'
  ];

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleRatingChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      rating: newValue
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.grade) {
      return;
    }

    if (!formData.rating || formData.rating < 1) {
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setLoading(true);
      setShowConfirm(false);
      
      if (isEdit) {
        // Update existing proposed grade
        await apiClient.put(`/api/walls/${wallId}/proposed-grades`, formData);
        setResultData({
          message: 'Grado proposto aggiornato con successo!',
          severity: 'success'
        });
      } else {
        // Create new proposed grade
        await apiClient.post(`/api/walls/${wallId}/proposed-grades`, formData);
        setResultData({
          message: 'Grado proposto aggiunto con successo!',
          severity: 'success'
        });
      }
      
      setShowResult(true);
      
      // Reset form
      setFormData({
        grade: '',
        notes: '',
        rating: 1
      });
      
      // Chiama onSuccess per aggiornare i dati
    //   onSuccess?.();
    } catch (err) {
      console.error('Errore nell\'operazione del grado proposto:', err);
      
      let errorMessage = isEdit ? 'Errore nell\'aggiornamento del grado proposto' : 'Errore nell\'aggiunta del grado proposto';
      if (err.response?.status === 409) {
        errorMessage = 'Hai già proposto un grado per questa via';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setResultData({
        message: errorMessage,
        severity: 'error'
      });
      setShowResult(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handleResultClose = () => {
    setShowResult(false);
    // Chiudi sempre il modal principale dopo aver mostrato il risultato
    onClose();
    onSuccess()
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        grade: '',
        notes: '',
        rating: 1
      });
      setShowConfirm(false);
      setShowResult(false);
      setResultData({ message: '', severity: 'info' });
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          {isEdit ? 'Modifica Grado Proposto' : 'Aggiungi Grado Proposto'}
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Grado Proposto</InputLabel>
              <Select
                value={formData.grade}
                onChange={handleChange('grade')}
                label="Grado Proposto"
              >
                {availableGrades.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Note (opzionale)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
              placeholder="Aggiungi le tue note sulla via..."
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.notes.length}/200 caratteri`}
            />

            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <span>Bellezza *:</span>
                <Rating
                  value={formData.rating}
                  onChange={handleRatingChange}
                  precision={1}
                  size="large"
                />
                <span>({formData.rating}/5)</span>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Annulla
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formData.grade || !formData.rating || formData.rating < 1}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? (isEdit ? 'Aggiornando...' : 'Aggiungendo...') : (isEdit ? 'Aggiorna Grado' : 'Aggiungi Grado')}
          </Button>
        </DialogActions>
      </form>

      <ModalConfirm
        open={showConfirm}
        title={isEdit ? "Conferma Modifica Grado Proposto" : "Conferma Grado Proposto"}
        message={`Sei sicuro di voler ${isEdit ? 'aggiornare' : 'aggiungere'} il grado "${formData.grade}" con valutazione ${formData.rating}/5?${formData.notes ? `\n\nNote: "${formData.notes}"` : ''}`}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />

      <ModalResult
        open={showResult}
        onClose={handleResultClose}
        message={resultData.message}
        severity={resultData.severity}
        title={resultData.severity === 'success' ? 'Successo' : 'Errore'}
      />
    </Dialog>
  );
};

export default ModalProposedGrade;
